import { isVideoType } from '../videoPlayerTypes';
const isIdParts = (s) => [5, 6].includes(s.length) && isVideoType(s[4]);
// Split ExternalId string into its constituent parts. Throws exceptions if the
// id is invalid.
//
// Valid ids should be in the form:
//     "<account id>:<player>:<embed>:<media id>:<'video'|'playlist'>:<autoplay>"
const splitId = (externalId) => {
    if (externalId == null)
        throw new Error('tmlExternalId required');
    const parts = externalId.split(':');
    if (!isIdParts(parts))
        throw new TypeError('invalid Brightcove tmlExternalId');
    return parts;
};
// Decode the Brightcove tml-external-id. Throws exceptions if the id is invalid.
// Returns a BrightCoveParameters object with the named properties.
export const decodeTmlExternalId = (brightcoveConfig, externalId) => {
    if (brightcoveConfig == null)
        throw new Error('brightcoveConfig required');
    if (!externalId)
        throw new Error('tmlExternalId required');
    // current lint rules prevent using let in this destructure. we could adopt
    // 'prefer-const': ['error', { destructuring: 'all' }],
    // but until then this is a two-liner
    const [accountId, _, embed, mediaId, type, hasAutoplay] = splitId(externalId);
    let [, player] = splitId(externalId);
    const autoPlay = hasAutoplay?.toLowerCase() === 'autoplay';
    if (accountId === brightcoveConfig.accountId) {
        player = brightcoveConfig.playerId;
    }
    return {
        accountId,
        player,
        embed,
        mediaId,
        type,
        autoPlay,
    };
};
// Validate a ContentVideo or throw an exception.
const validateContentVideo = (contentVideo) => {
    if (contentVideo == null)
        throw new Error('contentVideo required');
    if (!['brightcove', 'brightcovePlaylist'].includes(contentVideo.videoPlatformCode)) {
        throw new Error('not a Brightcove ContentVideo: ' + contentVideo.videoPlatformCode);
    }
};
// Gather the Brightcove video parameters from a Brightcove config and a contentVideo.
// Throws if parameters are missing or invalid.
export const getVideoParameters = (brightcoveConfig, contentVideo) => {
    if (brightcoveConfig == null)
        throw new Error('brightcoveConfig required');
    validateContentVideo(contentVideo);
    return {
        accountId: brightcoveConfig.accountId,
        player: brightcoveConfig.playerId,
        embed: 'default',
        mediaId: contentVideo.videoPlatformId,
        type: contentVideo.videoPlatformCode === 'brightcove' ? 'video' : 'playlist',
        // default. Potentially overridden by site.config.video.autoPlay
        autoPlay: false,
    };
};
// Return the Brightcove video URL given the VideoParameters.
const parametersToUrl = (parts, schemeless) => {
    const type = parts.type === 'video' ? 'videoId' : 'playlistId';
    // TEMPEST-2671: disable initial autoplay for the video embed;
    // we will autoplay once angular is fully loaded...
    const autoplay = parts.autoPlay ? '&phx-autoplay=true' : '';
    const queryStr = type + '=' + parts.mediaId + autoplay;
    const scheme = schemeless ?? false ? '' : 'https:';
    return (`${scheme}//players.brightcove.net/${parts.accountId}/` +
        `${parts.player}_${parts.embed}/index.html?${queryStr}`);
};
// Return the Brightcove video URL given the 5 parts tml-external-id. Throws
// exceptions if parameters are invalid.
export const tmlExternalIdToUrl = (brightcoveConfig, tmlExternalId, schemeless) => parametersToUrl(decodeTmlExternalId(brightcoveConfig, tmlExternalId), schemeless);
// Return the Brightcove video URL from the brightcoveConfig and a contentVideo.
// Throw exceptions if parameters are invalid.
export const contentVideoToUrl = (videoConfig, contentVideo, schemeless) => {
    const parts = getVideoParameters(videoConfig.brightcove, contentVideo);
    // Site config for video should dictate the auto play behavior
    // for any ContentVideo object.
    parts.autoPlay = videoConfig.autoPlay ?? false;
    return parametersToUrl(parts, schemeless);
};
//# sourceMappingURL=index.js.map